// Service centralisé pour les appels API
import { authAPI, ordersAPI, dashboardAPI } from "../../api/endpoints";
import { normalizeApiUser } from "../../utils/apiHelpers";
import { parseLoginResponse, parseProfileResponse } from "./schemas/auth";
import { clearLocalCartStorage } from "../../utils/cartStorage";

const STORAGE_KEYS = {
  token: "access_token",
  user: "user",
};

const isBrowser = typeof window !== "undefined";

const getStoredItem = (key) => {
  if (!isBrowser) return null;
  const localValue = window.localStorage.getItem(key);
  return localValue ?? window.sessionStorage.getItem(key);
};

const setStoredItem = (key, value) => {
  if (!isBrowser) return;
  window.localStorage.setItem(key, value);
  window.sessionStorage.setItem(key, value);
};

const removeStoredItem = (key) => {
  if (!isBrowser) return;
  window.localStorage.removeItem(key);
  window.sessionStorage.removeItem(key);
};

const parseStoredUser = () => {
  const storedUser = getStoredItem(STORAGE_KEYS.user);
  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch (error) {
    console.error('Impossible de parser les informations utilisateur stockées:', error);
    removeStoredItem(STORAGE_KEYS.user);
    return null;
  }
};

// Service d'authentification
export const authService = {
  // Connexion
  async login(email, password, recaptchaToken) {
    try {
      const response = await authAPI.login({
        email,
        password,
        recaptchaToken,
      });

      // Validation Zod (L-7) : avertit en dev si la gateway change de forme.
      // N'altère pas la valeur retournée — les appelants lisent response.data.
      parseLoginResponse(response.data);
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur de connexion' 
      };
    }
  },

  // Inscription
  async register(userData) {
    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur d\'inscription' 
      };
    }
  },

  async registerClient(clientData) {
    try {
      const response = await authAPI.registerClient(clientData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la création du compte client",
      };
    }
  },

  // Déconnexion
  async logout() {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le localStorage
      removeStoredItem(STORAGE_KEYS.token);
      removeStoredItem(STORAGE_KEYS.user);
      clearLocalCartStorage();
      if (isBrowser) {
        window.localStorage.removeItem('remember_me');
      }
    }
  },

  // Vérifier si l'utilisateur est connecté
  isAuthenticated() {
    return !!getStoredItem(STORAGE_KEYS.token);
  },

  // Obtenir l'utilisateur actuel en appelant l'API
  async getCurrentUser({ forceRefresh = true } = {}) {
    if (!this.isAuthenticated()) {
      removeStoredItem(STORAGE_KEYS.user);
      return null;
    }

    try {
      const response = await authAPI.getProfile();
      // Validation Zod (L-7) : diagnostic seulement. `normalizeApiUser` reste
      // la source de vérité pour la forme de l'utilisateur stocké.
      parseProfileResponse(response?.data);
      const fetchedUser = normalizeApiUser(response?.data);

      if (fetchedUser) {
        setStoredItem(STORAGE_KEYS.user, JSON.stringify(fetchedUser));
        return fetchedUser;
      }

      return parseStoredUser();
    } catch (error) {
      console.error("Erreur lors de la récupération de l'utilisateur connecté:", error);
      return parseStoredUser();
    }
  },

  // Obtenir l'utilisateur stocké localement (sans appel API)
  getCachedUser() {
    return parseStoredUser();
  },

  // Obtenir le token
  getToken() {
    return getStoredItem(STORAGE_KEYS.token);
  },

  // Mot de passe oublié
  async forgotPassword(email) {
    try {
      const response = await authAPI.forgotPassword(email);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erreur lors de l\'envoi de l\'email de réinitialisation' 
      };
    }
  },

  // Vérifier le token de réinitialisation
  async verifyResetToken(token) {
    try {
      const response = await authAPI.verifyResetToken(token);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Token invalide ou expiré' 
      };
    }
  },

  // Réinitialiser le mot de passe (dashboard/vendor, token dans l'URL)
  async resetPassword(token, password, confirmPassword) {
    try {
      const response = await authAPI.resetPassword(token, { password, confirmPassword });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe' 
      };
    }
  },

  // Valider le token (client) — GET /auth/validate-reset-token?token=xxx
  // Accepte { valid, email } en racine ou dans data (ex: { data: { valid, email } })
  async validateResetTokenForClient(token) {
    try {
      const response = await authAPI.validateResetToken(token);
      const body = response.data ?? response;
      const payload = body.data ?? body;
      const valid = payload.valid === true || body.valid === true;
      const email = payload.email || body.email || payload.user?.email || body.user?.email || null;
      return { success: true, valid, email, data: payload };
    } catch (error) {
      return { 
        success: false, 
        valid: false, 
        email: null,
        message: error.response?.data?.message
      };
    }
  },

  // Réinitialiser le mot de passe (client) — POST /auth/reset-password { email, code, newPassword }
  async resetPasswordWithCode(email, code, newPassword) {
    try {
      const response = await authAPI.resetPasswordWithBody({ email, code, newPassword });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message
      };
    }
  },

  // Mettre à jour le profil utilisateur
  async updateProfile(userId, data) {
    try {
      const response = await authAPI.updateProfile(userId, data);
      const body = response?.data;
      const updatedUser =
        body?.data ||
        body?.user ||
        (body?.id || body?.email ? body : null);

      if (updatedUser) {
        const cached = parseStoredUser();
        const merged = { ...(cached || {}), ...updatedUser };
        setStoredItem(STORAGE_KEYS.user, JSON.stringify(merged));
      }

      return { success: true, data: response.data, user: updatedUser };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Erreur lors de la mise à jour du profil',
      };
    }
  },

  // Récupérer un utilisateur par ID
  async getUserById(userId) {
    const response = await authAPI.getUser(userId);
    return normalizeApiUser(response.data);
  },

  // Changer le mot de passe (utilisateur connecté)
  async changePassword(newPassword, confirmPassword) {
    try {
      const response = await authAPI.changePassword({
        newPassword,
        confirmPassword,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors du changement de mot de passe",
      };
    }
  },

  // Connexion avec Google
  async loginWithGoogle(googleToken) {
    try {
      const response = await authAPI.loginWithGoogle(googleToken);
      parseLoginResponse(response.data);
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Erreur lors de la connexion avec Google'
      };
    }
  },
};

// Service des commandes
export const orderService = {
  // Obtenir toutes les commandes
  async getOrders(filters = {}) {
    try {
      const response = await ordersAPI.getOrders(filters);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des commandes' 
      };
    }
  },

  // Obtenir une commande
  async getOrder(id) {
    try {
      const response = await ordersAPI.getOrder(id);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération de la commande' 
      };
    }
  },

  // Mettre à jour le statut d'une commande
  async updateOrderStatus(id, status) {
    try {
      const response = await ordersAPI.updateOrderStatus(id, status);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la mise à jour du statut' 
      };
    }
  },
};

// Service du dashboard
export const dashboardService = {
  // Obtenir les statistiques
  async getStats() {
    try {
      const response = await dashboardAPI.getStats();
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des statistiques' 
      };
    }
  },

  // Obtenir les données de vente
  async getSales(period = 'month') {
    try {
      const response = await dashboardAPI.getSales(period);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des ventes' 
      };
    }
  },

  // Obtenir les produits les plus vendus
  async getBestSellingProducts() {
    try {
      const response = await dashboardAPI.getBestSellingProducts();
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des produits les plus vendus' 
      };
    }
  },

  // Obtenir les ventes par localisation
  async getSalesByLocation() {
    try {
      const response = await dashboardAPI.getSalesByLocation();
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des ventes par localisation' 
      };
    }
  },

  // Obtenir les catégories
  async getCategories() {
    try {
      const response = await dashboardAPI.getCategories();
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erreur lors de la récupération des catégories' 
      };
    }
  },
};
